import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';
import { WorkloadsGpuUtilizationSummary } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_GPU_UTILIZATION_SUMMARY_QUERY_KEY =
  'workloads-gpu-utilization-summary';

const getWorkloadsGpuUtilizationSummaryQueryKey = ({
  clusterId,
  startTime,
  endTime,
  sortField,
  sortOrder,
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit,
}: Partial<UseWorkloadsGpuUtilizationSummaryQueryProps> = {}) =>
  [
    WORKLOADS_GPU_UTILIZATION_SUMMARY_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          sortField,
          sortOrder,
          labels,
          namespaces,
          workloadNames,
          workloadTypes,
          startTime,
          endTime,
          pageLimit,
        ]
      : []),
  ] as const;

export type UseWorkloadsGpuUtilizationSummaryQueryProps = {
  clusterId: string;
  startTime: string;
  endTime: string;
  pageLimit?: number;
  labels?: Array<{ label?: string; value?: string }>;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  sortField?: string;
  sortOrder?: SortDirection;
  enabled?: boolean;
};

type UseWorkloadsGpuUtilizationSummaryQuery = {
  data?: WorkloadsGpuUtilizationSummary[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  count: number;
};

const getWorkloadsGpuUtilizationSummaryQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  sortField,
  sortOrder = 'desc',
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit = 50,
  enabled = true,
}: UseWorkloadsGpuUtilizationSummaryQueryProps) =>
  infiniteQueryOptions({
    queryKey: getWorkloadsGpuUtilizationSummaryQueryKey({
      clusterId,
      startTime,
      endTime,
      sortField,
      sortOrder,
      labels,
      namespaces,
      workloadNames,
      workloadTypes,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getWorkloadsGpuUtilizationSummary({
          filter: {
            clusterIds: [clusterId],
            workloadFilters: {
              labels,
              namespaces,
              workloadNames,
              workloadTypes,
            },
          },
          startTime,
          endTime,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField,
          sortOrder,
        });

      return data;
    },
    refetchOnWindowFocus: false,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
    enabled,
  });

export const useWorkloadsGpuUtilizationSummaryQuery = (
  props: UseWorkloadsGpuUtilizationSummaryQueryProps
): UseWorkloadsGpuUtilizationSummaryQuery => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getWorkloadsGpuUtilizationSummaryQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.workloads ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
  };
};
